/**
 * Upgrading old units for gold (ADR-0050).
 *
 * Without it an army raised in the bronze age is still the army three eras later —
 * which is exactly what AI-0's armies were in Phase 7a's measurements. A unit standing
 * in one of its owner's cities can be rebuilt as the newest step along its
 * `upgradeTo` chain that the owner knows how to build, for gold. It keeps its rank and
 * its share of health; it spends its turn being refitted.
 */

import { unitFor } from '../civs/traits.js';
import type { Content, UnitDef } from '../../../content/schema/index.js';
import { hasTech } from '../research/knowledge.js';
import type { Civ } from '../state/types.js';

/**
 * The furthest step along a unit's upgrade chain its owner can build, or null. `has` is
 * the strategic resources where it stands (ADR-0063): a step needing one it lacks is
 * passed over, but the walk goes on — a spearman without iron may still become a
 * musketeer.
 */
export function upgradeTarget(content: Content, civ: Civ, def: UnitDef, has = ~0): UnitDef | null {
  let best: UnitDef | null = null;
  let next = def.upgradeId;
  // Stop at the first step not yet known: a chain is walked in order, never skipped.
  // Each step is this civilisation's own version of it, unique unit included (ADR-0062).
  for (let guard = 0; next !== -1 && guard < 32; guard++) {
    const step = unitFor(content, civ, content.units.get(next));
    if (!hasTech(civ, step.techId)) break;
    if (step.resourceIds.every((r) => (has & (1 << r)) !== 0)) best = step;
    next = step.upgradeId;
  }
  return best;
}

/** Gold to upgrade one unit type to another. */
export function upgradeCost(content: Content, from: UnitDef, to: UnitDef): number {
  const rules = content.rules.combat;
  return rules.upgradeBase + Math.max(0, to.cost - from.cost) * rules.upgradePerShield;
}
